import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import Book3D from "@/components/Book3D";

const HEADLINE_LINES = ["HIGH SOMATIC", "MASTERY:", "ZERO CORTISOL"];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bookY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden geo-grid"
      data-testid="hero-section"
    >
      <motion.div
        aria-hidden="true"
        style={{ opacity: glowOpacity }}
        className="absolute top-1/3 right-[8%] w-[520px] h-[520px] rounded-full pointer-events-none"
      >
        <div className="w-full h-full rounded-full bg-[radial-gradient(circle,rgba(0,255,143,0.12)_0%,rgba(0,217,122,0.05)_40%,transparent_70%)]" />
      </motion.div>
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,#0A0A0A_85%)] pointer-events-none"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-[1.15fr_1fr] gap-16 lg:gap-8 items-center relative z-10">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#00FF8F] mb-8"
            data-testid="hero-overline"
          >
            A Doctrine by Joe Fenix
          </motion.p>

          <h1
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-white font-medium"
            data-testid="hero-headline"
          >
            {HEADLINE_LINES.map((line, i) => (
              <span className="mask-line" key={line}>
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.45 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className={i === HEADLINE_LINES.length - 1 ? "italic text-[#00FF8F]" : ""}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[#A6A6AD] font-light"
            data-testid="hero-subtitle"
          >
            The Somatic Blueprint to <strong className="font-semibold text-white">Override Stress</strong>,
            Ignite <strong className="font-semibold text-white">Endogenous GLP-1</strong>, and
            Maximize <strong className="font-semibold text-white">Mitochondrial Energy</strong>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <a
              href="#access"
              className="neon-btn inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] px-8 py-4 rounded-full"
              data-testid="hero-protocol-cta"
            >
              Begin the Descent
              <ArrowDown size={16} strokeWidth={2} />
            </a>
          </motion.div>
        </div>

        <motion.div
          style={{ y: bookY }}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="h-[420px] sm:h-[520px]"
          data-testid="hero-book-wrap"
        >
          <Book3D />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
        aria-hidden="true"
      >
        <span className="font-mono-tech text-[9px] uppercase tracking-[0.4em] text-[#6E6E76]">Scroll</span>
        <motion.div
          animate={{ scaleY: [1, 0.4, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-[#00FF8F] to-transparent origin-top"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
